import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowSquareDown, PencilSimple, TrashSimple, X } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { List, ListItem, ListText } from "@/components/ui/list"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { cn } from "@/lib/utils"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { ChMedicationDrawer } from "@/modules/patients/detail/medication/ch/ch-medication-drawer"
import { MedicationDetail } from "@/modules/patients/detail/medication/components/ch-medication-search"
import { ChMedicationSearchWrapper } from "@/modules/patients/detail/medication/components/ch-medication-search-wrapper"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { generateMedicationDescription } from "@/modules/patients/detail/medication/lib/generate-medication-description"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { handleMedicationSearchItem } from "@/modules/patients/detail/medication/services/useChMedicationSearchItemQuery"
import { useChMedicationContext } from "@/modules/patients/detail/medication/store/useChMedicationStore"
import { ChMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { ChRequestedMedications } from "@/modules/patients/detail/prescription/ch/components/ch-requested-medications"
import { Prescription, PrescriptionSchema } from "@/modules/patients/detail/prescription/ch/schema/prescription-schema"
import { chPrescriptionFragment$key } from "@/modules/patients/detail/prescription/ch/services/__generated__/chPrescriptionFragment.graphql"
import { chPrescriptionFragment } from "@/modules/patients/detail/prescription/ch/services/chPrescriptionFragment"
import { useChCreatePrescriptionMutation } from "@/modules/patients/detail/prescription/ch/services/useChCreatePrescriptionMutation"
import { useChDeletePrescriptionMutation } from "@/modules/patients/detail/prescription/ch/services/useChDeletePrescriptionMutation"
import { useChGeneratePrescriptionMutation } from "@/modules/patients/detail/prescription/ch/services/useChGeneratePrescriptionMutation"
import { useChUpdatePrescriptionMutation } from "@/modules/patients/detail/prescription/ch/services/useChUpdatePrescriptionMutation"
import { useChPrescriptionStoreContext } from "@/modules/patients/detail/prescription/ch/store/usePrescriptionStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import {
  DOCUMENT_LANGUAGES,
  getDocumentLanguageLabel,
  useDefaultDocumentLanguage,
} from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ChMedicationType } from "@/types/__generated__/union-types"

interface ChPrescriptionDrawerProps {
  progressNoteId: string
  id?: string
  prescriptionRef?: chPrescriptionFragment$key
  diagnosisCode: string
}

export const ChPrescriptionDrawer = withErrorBoundary(
  ({ prescriptionRef, progressNoteId, id, diagnosisCode }: ChPrescriptionDrawerProps) => {
    const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
    const { medgateServiceInfo } = useOutletContext<{ medgateServiceInfo: { genericsObligation: boolean } }>()
    const user = useUser()
    const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor"])
    const { t } = useTranslation()
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createPrescription, isCreating] = useChCreatePrescriptionMutation()
    const [updatePrescription, isUpdating] = useChUpdatePrescriptionMutation()
    const [deletePrescription] = useChDeletePrescriptionMutation()
    const [generatePrescriptionDocument, isGenerating] = useChGeneratePrescriptionMutation()
    const { documentLanguage } = useDefaultDocumentLanguage()
    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)
    const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
    const unSavedMedicationIds = useChPrescriptionStoreContext((state) => state.unSavedMedicationIds)
    const removedMedicationIds = useChPrescriptionStoreContext((state) => state.removedMedicationIds)
    const unSavedPrescriptionData = useChPrescriptionStoreContext((state) => state.unSavedPrescriptionData)
    const resetPrescriptionStore = useChPrescriptionStoreContext((state) => state.reset)
    const setUnSavedMedicationIds = useChPrescriptionStoreContext((state) => state.setUnSavedMedicationIds)
    const removeUnSavedMedicationId = useChPrescriptionStoreContext((state) => state.removeUnSavedMedicationId)
    const setUnSavedPrescriptionData = useChPrescriptionStoreContext((state) => state.setUnSavedPrescriptionData)
    const setRemovedMedicationIds = useChPrescriptionStoreContext((state) => state.setRemovedMedicationIds)
    const suggestedMedications = useChPrescriptionStoreContext((state) => state.suggestedMedications)
    const setChMedicationDrawerMode = useChMedicationContext((state) => state.setChMedicationDrawerMode)
    const patientId = usePatientParam()
    const appointmentId = useAppointmentParam()
    const contextAppointment = useContextAppointment()
    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chPrescriptionFragment, prescriptionRef ?? null)
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy
    const isFirstTimeRef = useRef(true)
    const abortControllerRef = useRef<AbortController | null>(null)
    const [medicationSearchText, setMedicationSearchText] = useState<string | null>(null)

    const { activeMedications, inactiveMedications } = useMedications<ChMedicationFragmentInlineList>()
    const persistedMedicationIds = persistedData?.medications.length ? persistedData.medications.map((i) => i.id) : []

    const getFilteredMedicationIds = useCallback(
      (medicationIds: string[]) => {
        const filteredMedicationIds = medicationIds.filter((id) => {
          // When existing medications get removed -> Filter those ones out from persisted medication ids
          return !removedMedicationIds.includes(id)
        })

        // Add items from un-saved medication ids to persisted medication ids
        const updatedMedicationIds = Array.from(new Set([...filteredMedicationIds, ...unSavedMedicationIds]))
        return updatedMedicationIds
      },
      [unSavedMedicationIds, removedMedicationIds],
    )

    const parsedData =
      isEditMode && persistedData && isFirstTimeRef.current
        ? PrescriptionSchema.parse({
            ...persistedData,
            medicationIds: getFilteredMedicationIds(persistedMedicationIds),
          })
        : null

    const form = useForm<Prescription>({
      mode: "all",
      resolver: zodResolver(PrescriptionSchema),
      defaultValues:
        isEditMode && parsedData
          ? { ...parsedData }
          : { language: documentLanguage, healthcareProviderId: null, medicationIds: unSavedMedicationIds },
    })

    const {
      control,
      formState: { isDirty, errors },
      getValues,
      reset,
    } = form

    const hasUnsavedChanges = isDirty || unSavedMedicationIds.length > 0 || removedMedicationIds.length > 0
    useDirtyDrawerMaker(hasUnsavedChanges)

    useEffect(() => {
      isFirstTimeRef.current = false
    }, [])

    // Prefill un-saved data if exist
    useEffect(() => {
      if (unSavedPrescriptionData) {
        const { healthcareProviderId, language } = unSavedPrescriptionData
        reset({
          ...getValues(),
          healthcareProviderId,
          language,
        })
      }
    }, [unSavedPrescriptionData, getValues, reset])

    // This function is used to store un-saved Prescription data
    // When we change some fields in the form and move to another drawer then back to Prescription drawer
    // the un-saved data will be prefilled to the form
    const handleChangeDrawer = () => {
      if (isDirty) {
        const { healthcareProviderId, language } = getValues()
        setUnSavedPrescriptionData({ healthcareProviderId, language })
      }
    }

    const handleCloseDrawer = () => {
      resetPrescriptionStore()
      closeDrawer()
    }

    const onSubmit: SubmitHandler<Prescription> = (data) => {
      if (!progressNoteId) return

      if (isAddMode) {
        FaroInsightsWrapper.pushEvent("documents_prescription_save")
        createPrescription({
          variables: {
            input: {
              patientId,
              progressNoteId,
              language: data.language,
              healthcareProviderId: data.healthcareProviderId,
              medicationIds: data.medicationIds,
            },
          },
          onCompleted(response) {
            const id = response.ch_createPrescription.ch_Prescription?.id
            if (id) {
              handleGeneratePrescription(id)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const prescription = data?.ch_createPrescription?.ch_Prescription

              if (prescription?.id) {
                const newRecordProxy = store.get(prescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_prescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode && id) {
        FaroInsightsWrapper.pushEvent("documents_prescription_save")
        updatePrescription({
          variables: {
            input: {
              id: id,
              language: data.language,
              healthcareProviderId: data.healthcareProviderId,
              medicationIds: data.medicationIds,
            },
          },
          onCompleted(response) {
            const id = response.ch_updatePrescription.ch_Prescription?.id
            if (id) {
              handleGeneratePrescription(id)
            }
          },
        })
      }
    }

    const handleGeneratePrescription = (id: string) => {
      if (id) {
        generatePrescriptionDocument(false, {
          id,
          progressNoteId,
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deletePrescription({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.prescription.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.prescription.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const formatMedicationGroup = useCallback(
      (selectedIds: string[]) => {
        const filteredActiveMedications = activeMedications.filter((m) => !selectedIds.includes(m.id))
        const filteredInactiveMedications = inactiveMedications.filter((m) => !selectedIds.includes(m.id))
        if (filteredActiveMedications.length === 0 && filteredInactiveMedications.length === 0) {
          return undefined
        }

        return [
          { title: t("patient.medication.active"), options: filteredActiveMedications },
          { title: t("patient.medication.inactive"), options: filteredInactiveMedications },
        ]
      },
      [activeMedications, inactiveMedications, t],
    )

    const getMedication = (
      id: string,
    ): {
      name: string
      description: string
      type: ChMedicationType | null
      medication?: ChMedicationFragmentInlineList[number]
    } => {
      const activeMedication = [...activeMedications].find((medication) => medication.id === id)
      const inactiveMedication = [...inactiveMedications].find((medication) => medication.id === id)
      const medication = activeMedication ?? inactiveMedication

      if (!medication) return { name: "", description: "", type: null }

      const { name, type } = medication

      const activeDescription = activeMedication && generateMedicationDescription({ isActive: true, ...medication })

      const inactiveDescription =
        inactiveMedication && generateMedicationDescription({ isActive: false, ...medication })

      const description = activeDescription ?? inactiveDescription ?? ""

      return { name, description, type, medication }
    }

    const handleEditMedication = async (editMedication?: ChMedicationFragmentInlineList[number]) => {
      if (!editMedication) return

      const { name, pharmaCode, type, templateId } = editMedication

      if (type === "PREPARATION" || type === "TEMPLATE") {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }
        abortControllerRef.current = new AbortController()

        const medicationDetail = await handleMedicationSearchItem(
          abortControllerRef.current.signal,
          type,
          patient.primaryLanguage,
          templateId,
          pharmaCode,
        )

        if (medicationDetail) {
          handleOpenMedicationDrawer({
            editMedication,
            medication: {
              ...medicationDetail,
              name,
              itemId: type === "TEMPLATE" ? (templateId ?? undefined) : (pharmaCode ?? undefined),
              pharmacode: pharmaCode,
              medicationType: type,
            },
          })
          return
        }
      }

      handleOpenMedicationDrawer({ editMedication })
    }

    const handleOpenMedicationDrawer = (options: {
      editMedication?: ChMedicationFragmentInlineList[number]
      freeTextMedicationName?: string
      medication?: MedicationDetail
    }) => {
      const { editMedication, freeTextMedicationName, medication } = options

      openDrawerWithoutConfirmation(
        () => (
          <ChMedicationDrawer
            prescriptionMedication={{
              editMedication,
              freeTextMedicationName,
              medication,
              progressNoteId,
              prescriptionRef,
              id,
            }}
            diagnosisCode={diagnosisCode}
          />
        ),
        "MedicationDrawer",
        { onBeforeOpen: () => !editMedication && setChMedicationDrawerMode(MEDICATION_MODES.CREATION) },
      )

      handleChangeDrawer()
    }

    const onFreeTextMedicationCreation = (freeTextMedicationName: string) => {
      handleOpenMedicationDrawer({ freeTextMedicationName })
    }

    const updateMedicationIdStore = (id: string) => {
      if (unSavedMedicationIds.includes(id)) removeUnSavedMedicationId(id)
      else setRemovedMedicationIds(id)
    }

    useEffect(() => {
      return () => abortControllerRef.current?.abort()
    }, [])

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="prescription-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.prescription.prescription")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="prescription-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({ field: { name, value, onChange }, formState: { errors }, fieldState: { invalid } }) => (
                    <div>
                      <Select value={value} onValueChange={onChange}>
                        <SelectTrigger hasError={invalid} className="w-56.5" data-testid="document-language-select">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.therapyPrescription.documentLanguage")}
                              value={t(getDocumentLanguageLabel(value))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="mb-8 flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.to")}</label>
                <div className="w-full">
                  <FormField
                    name="healthcareProviderId"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <CareProviderSearch
                        label={t("patient.prescription.pharmacy")}
                        searchType="PRESCRIPTION_DOCUMENT"
                        name={name}
                        value={value}
                        analyticsSectionName="document_prescription"
                        defaultAdvancedSearchFilters={{ groupType: "PHARMACY" }}
                        onSelect={onChange}
                        onRemove={() => onChange(null)}
                        error={errors.healthcareProviderId?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.prescription.medication")}</label>
                <div className="flex flex-col">
                  {suggestedMedications.length > 0 && (
                    <div className="mb-4 flex flex-col gap-2" data-testid="questionnaire-based-suggestion">
                      <p className="text-on-surface-subdued text-sm">
                        {t("patient.prescription.questionnaireBasedSuggestion")}
                      </p>
                      <div className="flex flex-col gap-0.5">
                        {suggestedMedications.map((medicationName) => (
                          <button
                            key={medicationName}
                            type="button"
                            data-testid="questionnaire-based-suggestion-item"
                            className="hover:bg-surface-hovered text-on-surface-subdued flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1"
                            onClick={() => setMedicationSearchText(medicationName)}>
                            <ArrowSquareDown className="shrink-0" />
                            <span className="truncate text-sm leading-5">{medicationName}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <ChRequestedMedications
                    requestedMedicationsRef={contextAppointment ?? null}
                    patientLanguage={patient.primaryLanguage}
                    onSelectRequestedMedication={(medication) => handleOpenMedicationDrawer({ medication })}
                  />
                  {medgateServiceInfo.genericsObligation && (
                    <Banner
                      data-testid="generics-obligation-banner"
                      type="inline"
                      variant="critical"
                      className="mb-4 flex items-center gap-1 rounded-md">
                      {t("patient.prescription.genericsObligation")}
                    </Banner>
                  )}
                  <FormField
                    name="medicationIds"
                    render={({ field: { value, onChange }, fieldState: { error } }) => {
                      const formattedOptions = formatMedicationGroup(value)

                      return appointmentId ? (
                        <Suspense fallback={<LoadingSpinner size={24} />}>
                          <ChMedicationSearchWrapper
                            includeTemplates
                            isPrescription
                            className="max-h-92.5 p-2"
                            patientLanguage={patient.primaryLanguage}
                            existingMedications={formattedOptions}
                            error={t(error?.message ?? "")}
                            handleSelectMedication={(medication) => {
                              onChange([...new Set([...value, medication.id])])
                              handleOpenMedicationDrawer({ medication })
                            }}
                            handleFreeTextMedicationSelection={onFreeTextMedicationCreation}
                            handleSelectExistingMedication={(editMedication) => {
                              onChange([...new Set([...value, editMedication.id])])
                              setUnSavedMedicationIds(editMedication.id!)
                              handleChangeDrawer()
                            }}
                            medicationSearchText={medicationSearchText}
                            setMedicationSearchText={setMedicationSearchText}
                            patientId={patientId}
                            appointmentId={appointmentId}
                            diagnosisCode={diagnosisCode}
                          />
                        </Suspense>
                      ) : (
                        <div></div>
                      )
                    }}
                  />

                  <FormField
                    name="medicationIds"
                    render={({ field: { value, onChange } }) => {
                      return (
                        <div className="mt-4">
                          <List asChild>
                            <>
                              {value.map((id: string) => {
                                const { name, description, medication, type } = getMedication(id)

                                return (
                                  <ListItem key={id} asChild>
                                    <div
                                      data-testid="list-item"
                                      className="hover:bg-surface-hovered relative min-h-12 items-center">
                                      <div className="min-w-0">
                                        <ListText
                                          className={cn("truncate", isFreeTextMedicationType(type) && "pr-1 italic")}>
                                          {name}
                                        </ListText>
                                        <ListText variant="secondary" className="truncate">
                                          {description}
                                        </ListText>
                                      </div>
                                      <div className="ml-auto hidden group-hover:flex group-hover:gap-1">
                                        <button
                                          className="hover:bg-surface-hovered active:enabled:bg-surface-pressed flex h-9 w-9 items-center justify-center hover:rounded-full"
                                          onClick={() => {
                                            const medications = new Set([...value])
                                            medications.delete(id)
                                            onChange([...medications])
                                            updateMedicationIdStore(id)
                                          }}>
                                          <X className="text-on-surface-subdued" />
                                        </button>
                                        <button
                                          className="hover:bg-surface-hovered active:enabled:bg-surface-pressed flex h-9 w-9 items-center justify-center hover:rounded-full disabled:cursor-not-allowed"
                                          onClick={() => handleEditMedication(medication)}>
                                          <PencilSimple className="text-on-surface-subdued" />
                                        </button>
                                      </div>
                                    </div>
                                  </ListItem>
                                )
                              })}
                            </>
                          </List>
                        </div>
                      )
                    }}
                  />
                </div>
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="prescription-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button onClick={handleDelete} data-testid="prescription-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button data-testid="prescription-cancel-button" variant="secondary" onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={!hasSavePermission}
                  data-testid="prescription-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_prescription_pharmacy_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChPrescriptionDrawer",
)
